import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmInfo from './confirm-info';
import AudioVideoCheck from './audio-video-check';
import SpeedTest from './speedtest';
import { setOnboardingTab, setOnboardingComplete, setOnboardingRoute } from '../../stores/actions/session';
import { setUseMobile } from '../../stores/actions/video';
import { setJoinRequest, loadInvitations } from '../../stores/actions/invitations';
import { replaceState } from '../../stores/actions/settings';
import Socket from '../../connection/socket';
import { ONBOARDING_ROUTES } from '../../stores/session-status';
import WaitingIndicator from '../general-components/waiting-indicator';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import * as Alerts from '../general-components/alert-service';
import {
	CheckJoinRequest,
	GetRequiresApproval,
	JoinRequest,
} from '../../connection/join-request';
import { GreenroomState as GS } from '../../stores/types/types';
import { getStateFromSettingsJson } from '../../utils/sync';
import useMediaDevices from '../../hooks/use-media-devices';

interface Props {
	// props that will be passed to this component
	socket: Socket | null;
}

const ONBOARDING_TABS = {
	INFO: 0,
	AV: 1,
	SPEED: 2,
};

const tabs = [
	'one',
	'two',
	'three',
];

export default function Onboarding(props: Props) {
	const onboardingTab = useSelector((s: GS) => s.SessionReducer.onboardingTab);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const skipApproval = useSelector((s: GS) => s.SessionReducer.skipApproval);
	const watcherOnly = useSelector((s: GS) => s.SessionReducer.watcher_only);
	const restrictedUserState = useSelector((s: GS) => s.SessionReducer.restrictedUserState);
	const liveChannels = useSelector((s: GS) => s.ContentReducer.liveChannels);
	const mainVideoStream = useSelector((s: GS) => s.VideoReducer.mainVideoStream);
	const useMobile = useSelector((s: GS) => s.VideoReducer.useMobile);
	const joinRequest = useSelector((s: GS) => s.InvitationsReducer.joinRequest);
	const entryWithoutProducer = useSelector((s: GS) => s.SettingsReducer.entryWithoutProducer);
	const askToJoin = useSelector((s: GS) => s.SettingsReducer.askToJoin);

	const { socket } = props;

	const dispatch = useDispatch();
	const { checkMediaDevices } = useMediaDevices();

	const [devicesChecked, setDevicesChecked] = useState<boolean>(false);
	const [testDone, setTestDone] = useState<boolean>(false);
	const [waiting, setWaiting] = useState<boolean>(false);
	const joinRequestCheckInverval = useRef<NodeJS.Timeout | null>(null);

	function goBack() {
		if (onboardingTab > 0) {
			dispatch(setOnboardingTab(onboardingTab - 1));
		}
		if (useMobile && onboardingTab === ONBOARDING_TABS.SPEED) {
			dispatch(setUseMobile(false));
		}
	}

	async function join() {
		try {
			if (!token || !meeting) return;

			if (!watcherOnly && !mainVideoStream && !restrictedUserState) {
				setWaiting(false);
				Alerts.showAlert({
					message: 'Unable to join Greenroom',
					description: 'Please set your camera and microphone permissions before entering.',
					type: 'neutral',
					duration: 5000,
				});
				return;
			}

			// check if this event requires approval
			const { approval_required: required } = await GetRequiresApproval(token, meeting.meeting);

			if (required) {
				// check whether or not we have a previous approval
				const { approved } = await CheckJoinRequest(token, meeting.meeting);

				// if approved, load the invitations and set onboarding to complete to load the main UI
				if (approved) {
					window.history.pushState(undefined, 'Greenroom', `/${meeting.meeting_id}`);
					dispatch(loadInvitations(token, meeting.meeting));
					dispatch(setOnboardingComplete(true));
				} else {
					// if not approved, send a join request and wait
					const { join_request: request } = await JoinRequest(token, meeting.meeting);
					dispatch(setJoinRequest(request));
				}
			} else {
				// approval not required, load invitations, set onboarding to complete to load the main UI
				window.history.pushState(undefined, 'Greenroom', `/${meeting.meeting_id}`);
				dispatch(loadInvitations(token, meeting.meeting));
				dispatch(setOnboardingComplete(true));
			}
		} catch (e) {
			console.error(e);
			Alerts.showAlert({
				message: 'Unable to Join',
				description: 'We ran into an issue checking permissions to join the greenroom. Please try again.',
				type: 'error',
				duration: 7000,
			});
		}
	}

	function next() {
		if (watcherOnly) {
			setWaiting(true);
			join();
		} else if (onboardingTab < 2) {
			dispatch(setOnboardingTab(onboardingTab + 1));
		} else if (meeting) {
			setWaiting(true);
			join();
		} else {
			dispatch(setOnboardingRoute(ONBOARDING_ROUTES.PRESENTER_SELECT_MEETING));
		}
	}

	function testComplete() {
		setTestDone(true);
	}

	function shouldButtonBeDisabled(): boolean {
		return onboardingTab === ONBOARDING_TABS.SPEED
		&& !!meeting?.meeting
		&& !liveChannels?.includes(meeting?.meeting)
		&& !entryWithoutProducer;
	}

	async function checkJoinRequest(request: number) {
		if (request && token && meeting) {
			const { approved } = await CheckJoinRequest(token, meeting.meeting);

			if (approved) {
				if (joinRequestCheckInverval.current) clearInterval(joinRequestCheckInverval.current);
				window.history.pushState(undefined, 'Greenroom', `/${meeting.meeting_id}`);
				dispatch(loadInvitations(token, meeting.meeting));
				dispatch(setOnboardingComplete(true));
			}
		}
	}

	useEffect(() => {
		if (socket && skipApproval && !watcherOnly) {
			dispatch(setOnboardingTab(1));
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [socket]);

	useEffect(() => {
		// producer dropped while awaiting join request
		if (meeting && !liveChannels.includes(meeting.meeting)) {
			if (entryWithoutProducer && askToJoin) return;
			setWaiting(false);
		}
	}, [liveChannels, meeting]);

	useEffect(() => {
		// forcing the join request to poll AND wait for websocket in case the connection is spotty
		if (joinRequest) {
			joinRequestCheckInverval.current = setInterval(() => checkJoinRequest(joinRequest), 3000);
		}

		return () => {
			if (joinRequestCheckInverval.current) clearInterval(joinRequestCheckInverval.current);
		};
	}, [joinRequest]);

	useEffect(() => {
		if (meeting) {
			dispatch(replaceState(getStateFromSettingsJson(meeting.settings_json)));
		}

		return () => {
			if (joinRequestCheckInverval.current) clearInterval(joinRequestCheckInverval.current);
		};
	}, []);

	useEffect(() => {
		if (!askToJoin) setWaiting(false);
	}, [askToJoin]);

	useEffect(() => {
		if (onboardingTab === ONBOARDING_TABS.AV && !devicesChecked) {
			setDevicesChecked(true);
			checkMediaDevices();
		}
	}, [onboardingTab]);

	return (
		<div className={'onboarding-tabs'}>
			<div className={classNames('tabs-container', tabs[onboardingTab])}>
				<div className="tab-wrapper one">
					<div className="scroll-container">
						<ConfirmInfo />
					</div>
				</div>
				{!watcherOnly && (
					<>
						<div className="tab-wrapper two">
							<div className="scroll-container">
								<AudioVideoCheck />
							</div>
						</div>
						<div className="tab-wrapper three">
							<div className="scroll-container">
								{onboardingTab === ONBOARDING_TABS.SPEED && (
									<SpeedTest testDone={testComplete} />
								)}
							</div>
						</div>
					</>
				)}
			</div>

			{askToJoin && joinRequest && waiting ? (
				<span className="waiting-message">You'll join when the producer lets you in </span>
			) : (
				<>
					{shouldButtonBeDisabled() && (
						<span className="waiting-message">Hang tight, the Producer will let you in.</span>
					)}
				</>
			)}

			<div className="tab-navigation">
				<div style={{ display: 'flex', justifyContent: 'flex-start', visibility: skipApproval ? 'hidden' : 'initial' }}>
					{onboardingTab > 0 ? (
						<button className="arrow" onClick={goBack}>
							<Icon name={ICONS.KEYBOARD_ARROW_LEFT} color={COLORS.WHITE} size={11}></Icon>
						</button>
					) : <div className="placeholder" />}
				</div>

				{!watcherOnly ? (
					<div className="current-tab-indicator" style={skipApproval ? { visibility: 'hidden' } : {}}>
						{useMobile && tabs.slice(0, 3).map((tn, ti) => (
							<div key={['tab', tn].join('-')} className={classNames('indicator', { active: onboardingTab === ti })}></div>
						))}
						{!useMobile && tabs.map((tn, ti) => (
							<div key={['tab', tn].join('-')} className={classNames('indicator', { active: onboardingTab === ti })}></div>
						))}
					</div>
				) : <div></div>}

				<button
					disabled={shouldButtonBeDisabled()}
					className={
						classNames(
							'next background-green font-weight-heavy',
							{
								done: (
									(watcherOnly && onboardingTab === ONBOARDING_TABS.INFO)
									|| (onboardingTab === ONBOARDING_TABS.AV && (meeting || testDone || waiting))
								),
							},
						)
					}
					onClick={next}
					style={{ justifySelf: 'flex-end' }}>
						{
							// eslint-disable-next-line no-nested-ternary
							(watcherOnly && onboardingTab === ONBOARDING_TABS.INFO)
							|| (onboardingTab === ONBOARDING_TABS.SPEED && (meeting || testDone || waiting))
								? (
									waiting ? <WaitingIndicator /> : 'Done'
								) : 'Next'
						}
				</button>
			</div>
		</div >
	);
}
